import { EditContextBody, EngineId } from './writeTypes';

/**
 * The transport denylist and the wire-id assertion (spec section 5.1 W1.6, 5.9 E2..E5, E15).
 *
 * The planner already refuses a publish-like or structural request by name (`plan.ts:93-94`,
 * thrown `:273-274`). This is the SECOND, independent refusal the spec asks for: nothing leaves the
 * write lane unless its method and path are one of the three routes Basil is allowed to use, and
 * every id in the body is an integer on the wire.
 *
 * The three allowed routes, and nothing else:
 *   PATCH /v1/stories/:storyId/edit-context          — id-addressed step and answer edits
 *   POST  /v1/stories/:storyId/branches              — create a node (whole-branch path)
 *   PUT   /v1/stories/:storyId/branches/:branchId    — resend a whole branch
 *
 * Never buildable here, by construction: any publish control, `PUT .../branches/:id/live`, the
 * retired per-step `PUT .../branches/:b/steps/:id` (AD1:13-25), and `PATCH .../branches/:b` with a
 * `steps` key.
 */

export type SendRefusalReason =
  | 'publish-like'
  | 'structural'
  | 'live-path'
  | 'per-step-put'
  | 'branch-patch-with-steps'
  | 'unknown-route'
  | 'non-integer-id';

/** A local refusal: it is thrown before any request is issued, and it carries the named reason. */
export class BasilSendRefusal extends Error {
  public readonly reason: SendRefusalReason;

  constructor(reason: SendRefusalReason, message: string) {
    super(message);
    this.name = 'BasilSendRefusal';
    this.reason = reason;
    // ES2016 target: restore the prototype chain so `instanceof` holds after transpilation.
    Object.setPrototypeOf(this, BasilSendRefusal.prototype);
  }
}

/** `plan.ts:93` — the frozen expression, kept as one literal. */
// eslint-disable-next-line max-len
const PUBLISH_LIKE = /publish|go[ _-]?live|update[ _-]?live|update[ _-]?in[ _-]?app|in[ _-]?app[ _-]?update|release|deploy|prepublish|submit|restore[ _-]?active|push|save[ _-]?as[ _-]?(?:template|prompt)|share|translate|gamepass/i;

/** `plan.ts:94` — delete, insert, move and reorder are refused by name (AD1:50). */
const STRUCTURAL_FORBIDDEN = /delete|remove|reorder|move|insert/i;

const EDIT_CONTEXT_ROUTE = /^\/?v1\/stories\/\d+\/edit-context$/;
const CREATE_BRANCH_ROUTE = /^\/?v1\/stories\/\d+\/branches$/;
const WHOLE_BRANCH_ROUTE = /^\/?v1\/stories\/\d+\/branches\/\d+$/;
const PER_STEP_ROUTE = /\/branches\/[^/]+\/steps\//;
const LIVE_ROUTE = /\/live$|\/golive|\/makepromptlive|\/updatelive/i;

export type BasilMethod = 'PATCH' | 'POST' | 'PUT';

export function editContextPath(storyId: number | string): string {
  return `/v1/stories/${storyId}/edit-context`;
}

export function createBranchPath(storyId: number | string): string {
  return `/v1/stories/${storyId}/branches`;
}

export function wholeBranchPath(storyId: number | string, branchId: number | string): string {
  return `/v1/stories/${storyId}/branches/${branchId}`;
}

/** The wire requires `Number.isInteger`: a numeric string is a 422 (`edit_context.js:314, :323, :332`). */
export function isWireId(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value);
}

function assertWireId(value: EngineId | undefined): void {
  if (!isWireId(value)) {
    throw new BasilSendRefusal('non-integer-id', "Basil can't tell which part of the script this change belongs to.");
  }
}

/** Every id in an edit-context body, node then step then answer, in the order they are sent. */
export function assertEditContextWireIds(body: EditContextBody): void {
  if (!body || !Array.isArray(body.nodes)) {
    throw new BasilSendRefusal('unknown-route', 'There are no scenes in this change to send.');
  }

  body.nodes.forEach((node) => {
    assertWireId(node.id);

    (node.steps || []).forEach((step) => {
      assertWireId(step.id);

      (step.answers || []).forEach((answer) => {
        assertWireId(answer.id);
      });
    });
  });
}

function hasStepsKey(body: unknown): boolean {
  return Boolean(body) && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'steps');
}

/**
 * Refuses, by name and before any request is issued, anything outside Basil's three routes.
 *
 * @param method the http method about to be used
 * @param path the request path, with or without a leading slash
 * @param body the request body, inspected only for the forbidden `steps` key on a branch PATCH
 */
export function assertSendable(method: string, path: string, body?: unknown): void {
  const normalized = String(path || '');
  const upper = String(method || '').toUpperCase();

  if (PUBLISH_LIKE.test(normalized)) {
    throw new BasilSendRefusal('publish-like', 'Basil never makes an episode live, so this change is paused.');
  }

  if (LIVE_ROUTE.test(normalized)) {
    throw new BasilSendRefusal('live-path', "Basil doesn't touch a live episode, so this change is paused.");
  }

  if (STRUCTURAL_FORBIDDEN.test(normalized)) {
    throw new BasilSendRefusal('structural', "Basil can't add, remove or move parts of the script yet.");
  }

  if (PER_STEP_ROUTE.test(normalized)) {
    throw new BasilSendRefusal('per-step-put', "Basil can't make this change, so nothing was sent.");
  }

  if (upper === 'PATCH' && !EDIT_CONTEXT_ROUTE.test(normalized)) {
    if (hasStepsKey(body)) {
      throw new BasilSendRefusal('branch-patch-with-steps', "Basil can't make a change like this one yet, so nothing was sent.");
    }

    throw new BasilSendRefusal('unknown-route', "Basil can't change this part of the episode, so nothing was sent.");
  }

  if (upper === 'PATCH') {
    return;
  }

  if (upper === 'POST' && CREATE_BRANCH_ROUTE.test(normalized)) {
    return;
  }

  if (upper === 'PUT' && WHOLE_BRANCH_ROUTE.test(normalized)) {
    return;
  }

  throw new BasilSendRefusal('unknown-route', "Basil can't make this change, so nothing was sent.");
}

/** True when a method and path pair would be refused; the test helper form of {@link assertSendable}. */
export function isSendable(method: string, path: string, body?: unknown): boolean {
  try {
    assertSendable(method, path, body);

    return true;
  } catch (error) {
    return false;
  }
}
